import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@mui/material";

import { BACKEND_ROUTES } from "../../../backendRoutes";
import { BookingTableSkeleton } from "./BookingTableSkeleton";
import { FetchErrorAlert } from "../../../components/FetchErrorAlert";
import { MONTHLY_ACQUISITION_SCALING } from "../../../constants";
import { useMemo } from "react";
import useSWR from "swr";
import { useWatch } from "react-hook-form";

/**
 * Min flight number values for color-coded booking table
 */
const COLOR_LIMITS = Object.freeze({
    GREEN: 1,
    YELLOW: 50,
    RED: 100,
});

export function BookingTable({ control }) {
    const [
        yearWatch,
        companyWatch,
        contractWatch,
        processingLeadWatch,
        numberDisplayedWatch,
        statusWatch,
        systemWatch,
        systemScalingWatch,
    ] = useWatch({
        name: [
            "year",
            "company",
            "contract",
            "processingLead",
            "numberDisplayed",
            "status",
            "system",
            "systemScaling",
        ],
        control,
    });
    const { data: bookingData, error: bookingDataFetchError } = useSWR(
        `${BACKEND_ROUTES.MONTHLY_ACQUISITION}/booking?scaling=${systemScalingWatch ? MONTHLY_ACQUISITION_SCALING.SYSTEM : MONTHLY_ACQUISITION_SCALING.CAMPAIGN}&year=${yearWatch}${
            companyWatch ? `&companyUuid=${companyWatch.uuid}` : ""
        }${contractWatch ? `&contractUuid=${contractWatch.uuid}` : ""}${
            statusWatch ? `&status=${statusWatch}` : ""
        }${
            systemWatch ? `&system=${systemWatch}` : ""
        }${processingLeadWatch ? `&processingLeadUuid=${processingLeadWatch.uuid}` : ""}`
    );

    const rows = useMemo(
        () => (bookingData ? createData(bookingData) : undefined),
        [bookingData]
    );

    const finalRow = useMemo(
        () => (rows ? dataSubTotals(rows) : undefined),
        [rows]
    );

    if (bookingDataFetchError)
        return <FetchErrorAlert error={bookingDataFetchError} />;

    if (!bookingData) {
        return <BookingTableSkeleton />;
    }

    return (
        <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }}>
                <TableHead>
                    <TableRow>
                        <TableCell
                            sx={{
                                bgcolor: (theme) => theme.palette.grey[210],
                            }}
                        >
                            {systemScalingWatch ? "System" : "Campaign"}
                        </TableCell>
                        <TableCell
                            align="center"
                            sx={{
                                bgcolor: (theme) => theme.palette.grey[210],
                            }}
                        >
                            Jan.
                        </TableCell>
                        <TableCell
                            align="center"
                            sx={{
                                bgcolor: (theme) => theme.palette.grey[210],
                            }}
                        >
                            Feb.
                        </TableCell>
                        <TableCell
                            align="center"
                            sx={{
                                bgcolor: (theme) => theme.palette.grey[210],
                            }}
                        >
                            Mar.
                        </TableCell>
                        <TableCell
                            align="center"
                            sx={{
                                bgcolor: (theme) => theme.palette.grey[210],
                            }}
                        >
                            Apr.
                        </TableCell>
                        <TableCell
                            align="center"
                            sx={{
                                bgcolor: (theme) => theme.palette.grey[210],
                            }}
                        >
                            May.
                        </TableCell>
                        <TableCell
                            align="center"
                            sx={{
                                bgcolor: (theme) => theme.palette.grey[210],
                            }}
                        >
                            Jun.
                        </TableCell>
                        <TableCell
                            align="center"
                            sx={{
                                bgcolor: (theme) => theme.palette.grey[210],
                            }}
                        >
                            Jul.
                        </TableCell>
                        <TableCell
                            align="center"
                            sx={{
                                bgcolor: (theme) => theme.palette.grey[210],
                            }}
                        >
                            Aug.
                        </TableCell>
                        <TableCell
                            align="center"
                            sx={{
                                bgcolor: (theme) => theme.palette.grey[210],
                            }}
                        >
                            Sep.
                        </TableCell>
                        <TableCell
                            align="center"
                            sx={{
                                bgcolor: (theme) => theme.palette.grey[210],
                            }}
                        >
                            Oct.
                        </TableCell>
                        <TableCell
                            align="center"
                            sx={{
                                bgcolor: (theme) => theme.palette.grey[210],
                            }}
                        >
                            Nov.
                        </TableCell>
                        <TableCell
                            align="center"
                            sx={{
                                bgcolor: (theme) => theme.palette.grey[210],
                            }}
                        >
                            Dec.
                        </TableCell>
                        <TableCell
                            align="center"
                            sx={{
                                bgcolor: (theme) => theme.palette.grey[210],
                            }}
                        >
                            Sub-total
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.concat(finalRow).map((row) => (
                        <TableRow
                            key={row.key}
                            sx={{
                                bgcolor: (theme) => theme.palette.grey[210],
                            }}
                        >
                            <TableCell
                                sx={{
                                    whiteSpace: "normal",
                                    wordBreak: "break-word",
                                }}
                                component="th"
                                scope="row"
                            >
                                {row.key}
                            </TableCell>
                            {[
                                "january",
                                "february",
                                "march",
                                "april",
                                "may",
                                "june",
                                "july",
                                "august",
                                "september",
                                "october",
                                "november",
                                "december",
                                "subtotal",
                            ].map((month) => {
                                return (
                                    <TableCell
                                        key={month}
                                        align="center"
                                        sx={{
                                            bgcolor: (theme) =>
                                                row[month] >= COLOR_LIMITS.RED
                                                    ? theme.palette.error.main
                                                    : row[month] >=
                                                        COLOR_LIMITS.YELLOW
                                                      ? theme.palette.warning
                                                            .main
                                                      : row[month] >=
                                                          COLOR_LIMITS.GREEN
                                                        ? theme.palette.success
                                                              .main
                                                        : theme.palette
                                                              .grey[300],
                                        }}
                                    >
                                        {numberDisplayedWatch && row[month]}
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function createData(bookingData) {
    const rows = [];
    Object.keys(bookingData).forEach((key) => {
        rows.push({
            key: key,
            january: bookingData[key][0],
            february: bookingData[key][1],
            march: bookingData[key][2],
            april: bookingData[key][3],
            may: bookingData[key][4],
            june: bookingData[key][5],
            july: bookingData[key][6],
            august: bookingData[key][7],
            september: bookingData[key][8],
            october: bookingData[key][9],
            november: bookingData[key][10],
            december: bookingData[key][11],
            subtotal: bookingData[key].reduce(
                (previous, current) => previous + current,
                0
            ),
        });
    });
    return rows;
}

function dataSubTotals(rows) {
    if (!rows.length)
        return {
            key: "Sub-total",
            january: 0,
            february: 0,
            march: 0,
            april: 0,
            may: 0,
            june: 0,
            july: 0,
            august: 0,
            september: 0,
            october: 0,
            november: 0,
            december: 0,
            subtotal: 0,
        };
    const months = Object.keys(rows[0]);
    return months.reduce((finalRow, month) => {
        if (month === "key") finalRow[month] = "Sub-total";
        else
            finalRow[month] = rows.reduce(
                (subTotal, row) => subTotal + row[month],
                0
            );
        return finalRow;
    }, {});
}
